import { Link } from '@chakra-ui/react';
import { SettingCardType } from '@monetize/types/app';
import { ReactNode, useState } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  doGetAccountingAuthorizeRedirectUrl,
  useGetAccountingConnections,
} from '../../../../api/settingsService';
import {
  MBox,
  MDivider,
  MGrid,
  MGridItem,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MText,
} from '../../../../components/Monetize';
import { SettingCardEdit } from '../../../../components/Settings/SettingCardEdit';
import { SettingCardEmpty } from '../../../../components/Settings/SettingCardEmpty';
import { useFlags } from '../../../../services/launchDarkly';
import { NetSuiteConnection } from './components/NetSuiteConnection';
import { NetsuiteForm } from './components/NetsuiteForm';
import { QuickBooksConnection } from './components/QuickBooksConnection';
import { QuickBooksForm } from './components/QuickBooksForm';
import { RilletConnection } from './components/RilletConnection';
import { RilletForm } from './components/RilletForm';
import { XeroConnection } from './components/XeroConnection';

type AccountingType = 'QUICKBOOKS' | 'NETSUITE' | 'XERO' | 'RILLET';

const QuickbooksDescription = (
  <MText mb="2">
    Connect your Quickbooks to sync your accounting data from MonetizeNow.
  </MText>
);

const NetsuiteDescription = (
  <MText mb="2">
    Connect to NetSuite to sync your accounting data from MonetizeNow. For more
    guidance, reference our documentation{' '}
    <Link
      href="https://docs.monetizenow.io/docs/authentication-setup"
      target="_blank"
      textDecoration="underline"
      rel="noopener noreferrer"
    >
      here.
    </Link>
  </MText>
);

export function Accounting() {
  const { showRilletConnector } = useFlags();
  const { data: connections, isLoading: isLoadingConnections } =
    useGetAccountingConnections();
  const [isXeroConnectorLoading, setIsXeroConnectorLoading] = useState(false);

  const [newAccounting, setNewAccounting] = useState<AccountingType | null>(
    null,
  );

  const handleXeroConnection = async () => {
    try {
      setIsXeroConnectorLoading(true);
      const { url } = await doGetAccountingAuthorizeRedirectUrl({
        type: 'xero',
      });
      window.location.href = url;
    } catch (error) {
      handleApiErrorToast(error);
    } finally {
      setIsXeroConnectorLoading(false);
    }
  };

  let providers: {
    key: AccountingType;
    type: SettingCardType;
    description: ReactNode;
    isLoading?: boolean;
    onClick: (() => void) | (() => Promise<void>);
    renderForm: () => ReactNode;
  }[] = [
    {
      key: 'QUICKBOOKS',
      type: 'QuickBooks',
      description: QuickbooksDescription,
      onClick: () => setNewAccounting('QUICKBOOKS'),
      renderForm: () => (
        <QuickBooksForm
          connection={connections?.quickbooks}
          onClose={() => setNewAccounting(null)}
        />
      ),
    },
    {
      key: 'NETSUITE',
      type: 'NetSuite',
      description: NetsuiteDescription,
      onClick: () => setNewAccounting('NETSUITE'),
      renderForm: () => (
        <NetsuiteForm
          connection={connections?.quickbooks as any}
          onClose={() => setNewAccounting(null)}
        />
      ),
    },
    {
      key: 'XERO',
      type: 'Xero',
      description: '',
      isLoading: isXeroConnectorLoading,
      onClick: handleXeroConnection,
      renderForm: () => <div />,
    },
  ];

  if (showRilletConnector) {
    providers.push({
      key: 'RILLET',
      type: 'Rillet',
      description: '',
      onClick: () => setNewAccounting('RILLET'),
      renderForm: () => <RilletForm onClose={() => setNewAccounting(null)} />,
    });
  }

  providers = providers.filter((provider) => {
    if (provider.key === 'QUICKBOOKS') {
      return !connections?.quickbooks;
    }
    if (provider.key === 'NETSUITE') {
      return !connections?.netsuite;
    }
    if (provider.key === 'XERO') {
      return !connections?.xero;
    }
    if (provider.key === 'RILLET') {
      return !connections?.rillet;
    }
    return true;
  });

  const providerConfigured =
    !!connections?.quickbooks ||
    !!connections?.netsuite ||
    !!connections?.xero ||
    !!connections?.rillet;

  const newProvider = newAccounting
    ? providers.find(({ key }) => key === newAccounting)
    : null;

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Accounting" />

      {isLoadingConnections && <MPageLoader />}

      {!isLoadingConnections && (
        <MBox maxW="600px">
          {!providerConfigured && !newAccounting && (
            <MText color="tGray.darkPurple" mb="4">
              Connect to an Accounting software to manage your accounting data
              from MonetizeNow.
            </MText>
          )}

          {newProvider && (
            <>
              {newProvider.description}
              <SettingCardEdit type={newProvider.type}>
                {newProvider.renderForm()}
              </SettingCardEdit>
            </>
          )}

          {connections?.quickbooks && (
            <MBox mb={6}>
              <QuickBooksConnection
                connection={connections.quickbooks}
                description={QuickbooksDescription}
              />
            </MBox>
          )}
          {connections?.netsuite && (
            <MBox mb={6}>
              <NetSuiteConnection
                connection={connections.netsuite}
                description={NetsuiteDescription}
              />
            </MBox>
          )}
          {connections?.xero && (
            <MBox mb={6}>
              <XeroConnection connection={connections.xero} />
            </MBox>
          )}
          {connections?.rillet && (
            <MBox mb={6}>
              <RilletConnection connection={connections.rillet} />
            </MBox>
          )}

          {providerConfigured && providers.length > 0 && (
            <>
              <MDivider my={6} />
              <MBox mt={4}>
                <MText fontWeight="600" fontSize="md">
                  Other Providers
                </MText>
                <MText>
                  In order to connect to another provider, you must disable your
                  current connection.
                </MText>
              </MBox>
            </>
          )}

          {!isLoadingConnections && !newAccounting && (
            <MGrid templateColumns="repeat(12, 1fr)" gap={10} mt={6}>
              {providers.map((provider) => (
                <MGridItem key={provider.key} colSpan={6}>
                  <SettingCardEmpty
                    isDisabled={providerConfigured}
                    type={provider.type}
                    isLoading={provider.isLoading}
                    onClick={provider.onClick}
                  />
                </MGridItem>
              ))}
            </MGrid>
          )}
        </MBox>
      )}
    </MPageContainer>
  );
}
